import { FOOTER_MAP } from '../components/footer/footer.const';
import { HEADER_MAP } from '../components/header/header.const';

export type BrandKey = 'windownation' | 'newsouth' | 'armorvue';

export interface BrandContent {
  introHeroTitle: string;
  introHeroDescription: string;
  introHeroPhoneDisplay: string;
  introHeroPhoneHref: string;
  introFormTitle: string;
  introFormDescription: string;
  introEmbeddedTitle: string;
  introEmbeddedDescription: string;
  introDefaultCta: string;
  introEmbeddedCta: string;
  agreementText: string;
  footerCompanySectionTitle: string;
  footerCopyright: string;
}

export interface BrandConfig {
  key: BrandKey;
  displayName: string;
  hostnames: string[];
  siteBaseUrl: string;
  appointmentBaseUrl: string;
  logoPath: string;
  logoAlt: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    textOnDark: string;
    darkBackground: string;
  };
  headerOverrides: {
    ctaTitle: string;
    ctaHref: string;
    homeUrl: string;
    showroomHref: string;
    salesDisplay?: string;
    salesHref?: string;
  };
  content: BrandContent;
}

const defaultBrandContent: BrandContent = {
  introHeroTitle: 'Ready for new windows or doors?',
  introHeroDescription:
    'Window Nation has helped homeowners like you get the top-quality products at an affordable price. It\'s easy - answer a few questions to help us serve you best or call us at',
  introHeroPhoneDisplay: '866-884-4127',
  introHeroPhoneHref: 'tel:8668844127',
  introFormTitle: 'Let\'s get started with your free estimate!',
  introFormDescription:
    'Don\'t worry - we won\'t sell your info. We just need it so we can contact you about your quote.',
  introEmbeddedTitle: 'Buy 2 Windows, Get 2 For Free',
  introEmbeddedDescription:
    'plus, $0 down, $0 monthly payments, and 0% interest rate until 2027',
  introDefaultCta: 'Start my estimate',
  introEmbeddedCta: 'Book Your Estimate Online',
  agreementText:
    'I agree to be contacted by Window Nation via call, email, and text. To opt-out, you can reply \"stop\" at any time or click the unsubscribe link in the emails. Message and data rates may apply. <a href="https://www.windownation.com/privacy-policy" target="_blank">Privacy Policy</a>',
  footerCompanySectionTitle: 'Window Nation',
  footerCopyright: '© 2026 Window Nation. All Rights Reserved.',
};

export const BRAND_CONFIGS: Record<BrandKey, BrandConfig> = {
  windownation: {
    key: 'windownation',
    displayName: 'Window Nation',
    hostnames: ['windownation.com'],
    siteBaseUrl: 'https://www.windownation.com',
    appointmentBaseUrl: 'https://appointment.windownation.com/',
    logoPath: '/assets/window-nation-knockout-logo.svg',
    logoAlt: 'Window Nation logo',
    colors: {
      primary: '#005ba7',
      secondary: '#4cb4e7',
      accent: '#ffc300',
      textOnDark: '#ffffff',
      darkBackground: '#000000',
    },
    headerOverrides: {
      ctaTitle: 'Request a Quote',
      ctaHref: '/request-a-call',
      homeUrl: 'https://www.windownation.com/',
      showroomHref: '/showrooms',
    },
    content: defaultBrandContent,
  },
  newsouth: {
    key: 'newsouth',
    displayName: 'NewSouth Window Solutions',
    hostnames: ['newsouthwindow.com'],
    siteBaseUrl: 'https://www.newsouthwindow.com',
    appointmentBaseUrl: 'https://appointment.windownation.com/',
    logoPath: '/assets/newsouth-logo.png',
    logoAlt: 'NewSouth Window Solutions logo',
    colors: {
      primary: '#0068b3',
      secondary: '#58b8e3',
      accent: '#e87722',
      textOnDark: '#ffffff',
      darkBackground: '#0d1b2a',
    },
    headerOverrides: {
      ctaTitle: 'Get a Free Estimate',
      ctaHref: 'https://newsouthwindow.com/estimate/',
      homeUrl: 'https://www.newsouthwindow.com/',
      showroomHref: 'https://newsouthwindow.com/locations/',
      salesDisplay: 'Sales: (866) 638-5831',
      salesHref: 'tel:8666385831',
    },
    content: {
      ...defaultBrandContent,
      introHeroPhoneDisplay: '866-638-5831',
      introHeroPhoneHref: 'tel:8666385831',
      introHeroDescription:
        'NewSouth Window Solutions helps homeowners upgrade with high-quality products and expert installation. Answer a few questions to get started, or call us at',
      agreementText:
        'I agree to be contacted by NewSouth Window Solutions via call, email, and text. To opt-out, you can reply \"stop\" at any time or click the unsubscribe link in the emails. Message and data rates may apply. <a href="https://www.newsouthwindow.com/privacy-policy" target="_blank">Privacy Policy</a>',
      footerCompanySectionTitle: 'NewSouth Window Solutions',
      footerCopyright:
        '© 2026 NewSouth Window Solutions. All Rights Reserved.',
    },
  },
  armorvue: {
    key: 'armorvue',
    displayName: 'ARMORVUE',
    hostnames: ['armorvue.com'],
    siteBaseUrl: 'https://www.armorvue.com',
    appointmentBaseUrl: 'https://appointment.windownation.com/',
    logoPath: '/assets/armorvue-logo.png',
    logoAlt: 'ARMORVUE logo',
    colors: {
      primary: '#10516d',
      secondary: '#6ec1e4',
      accent: '#d2c27a',
      textOnDark: '#ffffff',
      darkBackground: '#121b28',
    },
    headerOverrides: {
      ctaTitle: 'Book Free Consultation',
      ctaHref: '/request-a-call',
      homeUrl: 'https://www.armorvue.com/',
      showroomHref: 'https://armorvue.com/location-contact/',
      salesDisplay: 'Sales: (844) 277-8752',
      salesHref: 'tel:8442778752',
    },
    content: {
      ...defaultBrandContent,
      introHeroPhoneDisplay: '844-277-8752',
      introHeroPhoneHref: 'tel:8442778752',
      introHeroDescription:
        'ARMORVUE has helped homeowners transform their homes with durable windows and doors. Answer a few questions to get started, or call us at',
      agreementText:
        'I agree to be contacted by ARMORVUE via call, email, and text. To opt-out, you can reply \"stop\" at any time or click the unsubscribe link in the emails. Message and data rates may apply. <a href="https://www.armorvue.com/privacy-policy" target="_blank">Privacy Policy</a>',
      footerCompanySectionTitle: 'ARMORVUE',
      footerCopyright: '© 2026 ARMORVUE. All Rights Reserved.',
    },
  },
};

export const DEFAULT_BRAND_KEY: BrandKey = 'windownation';

export const isBrandKey = (value: string | null | undefined): value is BrandKey =>
  !!value && Object.keys(BRAND_CONFIGS).includes(value);

const matchesHostname = (hostname: string, domain: string): boolean => {
  const normalizedHostname = hostname.toLowerCase();
  const normalizedDomain = domain.toLowerCase();
  return (
    normalizedHostname === normalizedDomain ||
    normalizedHostname.endsWith(`.${normalizedDomain}`)
  );
};

const getBrandFromHostname = (hostname: string): BrandKey | null => {
  const normalizedHostname = hostname.toLowerCase();
  const brandKey = (Object.keys(BRAND_CONFIGS) as BrandKey[]).find((key) =>
    BRAND_CONFIGS[key].hostnames.some((domain) =>
      matchesHostname(normalizedHostname, domain)
    )
  );
  return brandKey ?? null;
};

export const resolveBrandKey = (
  hostname: string,
  queryBrand?: string | null
): BrandKey => {
  const hostnameBrand = getBrandFromHostname(hostname);
  if (hostnameBrand) {
    return hostnameBrand;
  }

  const normalizedQueryBrand = queryBrand?.toLowerCase() ?? null;
  if (isBrandKey(normalizedQueryBrand)) {
    return normalizedQueryBrand;
  }

  return DEFAULT_BRAND_KEY;
};

export const cloneFooterMap = () => JSON.parse(JSON.stringify(FOOTER_MAP));
export const cloneHeaderMap = () => JSON.parse(JSON.stringify(HEADER_MAP));
